import { Button } from "components/DesignSystem/Button/Button";
import { openDownloadUrl } from "util/openDownloadUrl";
import { Document } from "components/icons/Document";
import { FilePdf } from "components/icons/FilePdf";
import { DownloadIconV2 } from "components/icons/DownloadIconV2";
import { Preview } from "components/PreviewModal";
import { EntityDataAttribute } from "types/Models/entity";
import { StateRegistrationsRes } from "store/apis/StateRegistrations";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { useModal } from "hooks/useModal";
import { useState } from "react";
import { CollapsibleCard } from "./CollapsibleCard";

const DownloadButton = ({
  fileId,
  groupId,
}: {
  fileId: string;
  groupId: string;
}) => {
  const [fetchUrl, { isFetching }] = useLazyGetPreviewUrlQuery();

  const handleDownload = async () => {
    const result = await fetchUrl({ groupId, fileId });
    if (result.data?.download_url) {
      openDownloadUrl(result.data.download_url);
    }
  };

  return (
    <Button
      customType="ghost"
      size="xsmall"
      isLoading={isFetching}
      disabled={isFetching}
      onClick={handleDownload}
    >
      <DownloadIconV2 />
    </Button>
  );
};

const REGISTRATION_TYPES = [
  "SECRETARY_OF_STATE",
  "REGISTERED_AGENT",
  "PAY_ROLL",
] as const;

type Props = {
  entityAttributes: EntityDataAttribute[];
  stateDetail: StateRegistrationsRes;
};

const extractFileDocs = (attrs: EntityDataAttribute[]) =>
  attrs.flatMap((attr) => {
    if (attr.type !== "FILE" && attr.type !== "MULTI_FILE_INPUT") return [];
    const fileIds = Array.isArray(attr.value)
      ? (attr.value as string[]).filter(Boolean)
      : attr.value
      ? [attr.value as string]
      : [];
    return fileIds.map((fileId) => ({ label: attr.label, fileId }));
  });

export const DocumentsCard = ({ entityAttributes, stateDetail }: Props) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const previewModal = useModal();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const registrationDocs = REGISTRATION_TYPES.flatMap((type) =>
    extractFileDocs(stateDetail[type].data_attributes)
  );

  const entityDocs = stateDetail.is_home_state
    ? extractFileDocs(entityAttributes)
    : [];

  const docs = [...entityDocs, ...registrationDocs];

  if (docs.length === 0) return null;

  const openPreview = (fileId: string) => {
    setPreviewFileId(fileId);
    previewModal.open();
  };

  const closePreview = () => {
    previewModal.close();
    setPreviewFileId(null);
  };

  return (
    <>
      <CollapsibleCard
        title="Documents"
        icon={<Document width={16} height={16} />}
      >
        <div className="t-grid t-grid-cols-2 t-gap-3 t-p-5 sm:t-grid-cols-1 sm:t-p-4">
          {docs.map(({ label, fileId }) => (
            <div
              key={fileId}
              className="t-flex t-items-center t-gap-2 t-p-3 t-rounded-lg t-border t-border-solid t-border-neutral-10"
            >
              <span className="t-flex-shrink-0">
                <FilePdf />
              </span>
              <span
                className="t-flex-1 t-min-w-0 t-text-body-sm t-font-medium t-text-text-100 t-truncate t-cursor-pointer hover:t-underline"
                onClick={() => openPreview(fileId)}
              >
                {label}
              </span>
              <DownloadButton fileId={fileId} groupId={groupId} />
            </div>
          ))}
        </div>
      </CollapsibleCard>

      <Preview
        showModal={previewModal.isOpen}
        closeModal={closePreview}
        groupId={groupId}
        previewId={previewFileId}
      />
    </>
  );
};
